import { matchesAllTokens } from 'src/common/utils/textSearch';

export const SecretMenuTabs = {
  companies: 'companies',
  companyUsers: 'company-users',
  companyClients: 'company-clients',
  joinRequests: 'join-requests',
  tenantSettings: 'tenant-settings',
  tenantApiKeys: 'tenant-api-keys',
  exportAssets: 'export-assets',
  autoBrand: 'auto-brand',
  files: 'files',
  generatedDocuments: 'generated-documents',
  backgroundProcesses: 'background-processes',
  bulkValuations: 'bulk-valuations',
  calendarHealth: 'calendar-health',
  conversationDebugger: 'conversation-debugger',
  templates: 'templates',
  oldTemplates: 'old-templates',
  customSkills: 'custom-skills',
  productKnowledge: 'product-knowledge',
  llmConfig: 'llm-config',
  llmDispatcher: 'llm-dispatcher',
  modelArena: 'model-arena',
  info: 'info',
  healthCheck: 'health-check',
  featureFlags: 'feature-flags',
  ldFlagStatuses: 'ld-flag-statuses',
  flagMatrix: 'flag-matrix',
  botMigration: 'bot-migration',
  reprocessWrangled: 'reprocess-wrangled',
  sharepoint: 'sharepoint',
  openBanking: 'open-banking',
  microsoftEmail: 'microsoft-email',
  sandbox: 'sandbox',
  editorPlayground: 'editor-playground',
  topicTree: 'topic-tree',
  transcriptTester: 'transcript-tester',
  deriveFrnTest: 'derive-frn-test',
  autoBrandFromUrl: 'auto-brand-from-url',
  billing: 'billing',
  usageMetrics: 'usage-metrics',
} as const;

export type SecretMenuTab = (typeof SecretMenuTabs)[keyof typeof SecretMenuTabs];

export type SecretMenuAudience = 'ops' | 'dev';

export type SecretMenuMarker = 'dangerous' | 'deprecated';

export type SecretMenuGate =
  | 'afternoonOnly'
  | 'botMigrationFlag'
  | 'llmDispatcherFlag'
  | 'usageMetricsFlag';

type SecretMenuCategoryId =
  | 'ops'
  | 'tenant-data'
  | 'generation'
  | 'model-routing'
  | 'evaluation'
  | 'platform'
  | 'integrations'
  | 'playground';

type SecretMenuCategory = {
  id: SecretMenuCategoryId;
  audience: SecretMenuAudience;
  title: string;
  blurb: string;
};

export const SECRET_MENU_CATEGORIES: readonly SecretMenuCategory[] = [
  {
    id: 'ops',
    audience: 'ops',
    title: 'Operations',
    blurb: 'Tenant and user administration. Nothing here needs technical knowledge.',
  },
  {
    id: 'tenant-data',
    audience: 'dev',
    title: 'Tenant data',
    blurb: 'Open when something looks wrong for one specific customer.',
  },
  {
    id: 'generation',
    audience: 'dev',
    title: 'Generation and Siesta',
    blurb: 'Where prose you write becomes instructions the AI follows.',
  },
  {
    id: 'model-routing',
    audience: 'dev',
    title: 'Model routing',
    blurb: 'Reaches every tenant, and goes live without a deploy.',
  },
  {
    id: 'evaluation',
    audience: 'dev',
    title: 'Evaluation',
    blurb: 'Measures models against fixed cases. Changes nothing that serves clients.',
  },
  {
    id: 'platform',
    audience: 'dev',
    title: 'Platform',
    blurb: 'The system as a whole rather than one tenant.',
  },
  {
    id: 'integrations',
    audience: 'dev',
    title: 'Integrations (beta)',
    blurb:
      'Third-party connections not yet productised. Each one connects your own account to prove the integration works.',
  },
  {
    id: 'playground',
    audience: 'dev',
    title: 'Playgrounds and harnesses',
    blurb: 'Run a pipeline or a component against throwaway input.',
  },
];

type SecretMenuToolMetadata = {
  label: string;
  description: string;
  category: SecretMenuCategoryId;
  dangerous?: true;
  deprecated?: true;
  gate?: SecretMenuGate;
  keywords?: readonly string[];
};

/**
 * Written in the order tools appear on the index, grouped by category. Typed as a complete record
 * so adding a tab without describing it does not compile.
 */
const TOOL_METADATA: Record<SecretMenuTab, SecretMenuToolMetadata> = {
  [SecretMenuTabs.companies]: {
    label: 'Companies',
    description: 'Company records, FCA numbers and billing.',
    category: 'ops',
    keywords: ['tenant', 'firm', 'frn', 'finio', 'internal', 'smart create', 'docusign'],
  },
  [SecretMenuTabs.companyUsers]: {
    label: 'Company users',
    description: "Add, list and remove a tenant's users.",
    category: 'ops',
    dangerous: true,
    keywords: ['adviser', 'advisor', 'staff', 'role', 'invite', 'delete'],
  },
  [SecretMenuTabs.companyClients]: {
    label: 'Company clients',
    description: 'Client list and CSV bulk upload.',
    category: 'ops',
    keywords: ['import', 'spreadsheet'],
  },
  [SecretMenuTabs.joinRequests]: {
    label: 'Join requests',
    description: 'Review signups, provision tenants, send contracts.',
    category: 'ops',
    keywords: ['docusign', 'onboarding', 'sign up'],
  },
  [SecretMenuTabs.tenantSettings]: {
    label: 'Tenant settings',
    description: "How a tenant's users sign in.",
    category: 'ops',
    dangerous: true,
    keywords: ['sso', 'login', 'authentication', 'password', 'microsoft', 'google'],
  },
  [SecretMenuTabs.tenantApiKeys]: {
    label: 'Tenant API keys',
    description: 'Generate and revoke integration keys.',
    category: 'ops',
    keywords: ['origo', 'token', 'external api'],
  },
  [SecretMenuTabs.exportAssets]: {
    label: 'Export branding',
    description: 'Fonts, colours and document assets.',
    category: 'ops',
    dangerous: true,
    keywords: ['branding', 'palette', 'typography', 'logo', 'backpopulate', 'bot image'],
  },
  [SecretMenuTabs.autoBrand]: {
    label: 'Auto brand',
    description: 'Brand a tenant from a sample document.',
    category: 'ops',
    gate: 'afternoonOnly',
    keywords: ['branding', 'palette', 'logo', 'extract', 'title page'],
  },
  [SecretMenuTabs.files]: {
    label: 'Files',
    description: 'Uploaded files and how far each one got.',
    category: 'tenant-data',
    keywords: ['data upload', 'parsed', 'wrangled', 'entity detection', 'documents'],
  },
  [SecretMenuTabs.generatedDocuments]: {
    label: 'Generated documents',
    description: 'Reports, notes and agendas with their drafts.',
    category: 'tenant-data',
    keywords: ['suitability report', 'meeting notes', 'agenda', 'sections'],
  },
  [SecretMenuTabs.backgroundProcesses]: {
    label: 'Background processes',
    description: 'Job status and failure detail.',
    category: 'tenant-data',
    keywords: ['jobs', 'queue', 'failures'],
  },
  [SecretMenuTabs.bulkValuations]: {
    label: 'Bulk valuations',
    description: 'Provider matching and unprocessed updates.',
    category: 'tenant-data',
    keywords: ['finio', 'origo', 'assets', 'firm name'],
  },
  [SecretMenuTabs.calendarHealth]: {
    label: 'Calendar health',
    description: 'Local calendars checked against Recall.',
    category: 'tenant-data',
    dangerous: true,
    keywords: ['recall', 'bots', 'meetings', 'events', 'resync'],
  },
  [SecretMenuTabs.conversationDebugger]: {
    label: 'Conversation debugger',
    description: 'Every message Siesta saw, including tool calls.',
    category: 'tenant-data',
    keywords: ['siesta', 'chat', 'messages', 'tool calls'],
  },
  [SecretMenuTabs.templates]: {
    label: 'Templates',
    description: 'Report templates and their section instructions.',
    category: 'generation',
    keywords: ['suitability', 'prompts'],
  },
  [SecretMenuTabs.oldTemplates]: {
    label: 'Templates (old)',
    description: 'Raw template upload, superseded.',
    category: 'generation',
    deprecated: true,
    keywords: ['legacy'],
  },
  [SecretMenuTabs.customSkills]: {
    label: 'Custom skills',
    description: 'Per-tenant skills available to Siesta.',
    category: 'generation',
    keywords: ['siesta', 'agent', 'tools', 'skill definition'],
  },
  [SecretMenuTabs.productKnowledge]: {
    label: 'Product knowledge',
    description: 'What Siesta knows about the platform.',
    category: 'generation',
    keywords: ['siesta', 'articles', 'help'],
  },
  [SecretMenuTabs.llmConfig]: {
    label: 'LLM config',
    description: 'Model slots and fallback ordering.',
    category: 'model-routing',
    dangerous: true,
    keywords: ['model', 'providers', 'routing'],
  },
  [SecretMenuTabs.llmDispatcher]: {
    label: 'LLM dispatcher',
    description: 'Task pools and the model catalogue.',
    category: 'model-routing',
    dangerous: true,
    gate: 'llmDispatcherFlag',
    keywords: ['model', 'providers', 'routing', 'task key', 'modality'],
  },
  [SecretMenuTabs.modelArena]: {
    label: 'Model arena',
    description: 'Compare candidate models for a task before adding one to its pool.',
    category: 'evaluation',
    gate: 'llmDispatcherFlag',
    keywords: ['eval', 'benchmark', 'compare', 'leaderboard', 'candidates', 'arena', 'model'],
  },
  [SecretMenuTabs.info]: {
    label: 'Info',
    description: 'Deployed release version.',
    category: 'platform',
    keywords: ['build', 'deploy'],
  },
  [SecretMenuTabs.healthCheck]: {
    label: 'Health check',
    description: 'Database and LLM provider probes.',
    category: 'platform',
    keywords: ['system health', 'status'],
  },
  [SecretMenuTabs.featureFlags]: {
    label: 'Feature flags',
    description: 'Flag values, with local overrides in dev.',
    category: 'platform',
    keywords: ['launchdarkly', 'toggles'],
  },
  [SecretMenuTabs.ldFlagStatuses]: {
    label: 'LD flag statuses',
    description: 'LaunchDarkly rollout state per flag.',
    category: 'platform',
    keywords: ['launchdarkly', 'maintainer', 'stale'],
  },
  [SecretMenuTabs.flagMatrix]: {
    label: 'Flag matrix',
    description: 'Every flag evaluated against every tenant.',
    category: 'platform',
    keywords: ['launchdarkly', 'rollout', 'targeting', 'rules', 'simulate', 'internal'],
  },
  [SecretMenuTabs.botMigration]: {
    label: 'Bot migration',
    description: 'Rename scheduled bots across every company.',
    category: 'platform',
    dangerous: true,
    gate: 'botMigrationFlag',
    keywords: ['recall', 'siesta', 'meetings'],
  },
  [SecretMenuTabs.reprocessWrangled]: {
    label: 'Reprocess empty wrangles',
    description: 'Recover files left empty by the 3 Aug model outage.',
    category: 'platform',
    dangerous: true,
    keywords: [
      'wrangle',
      'recovery',
      'reprocess',
      'data upload',
      'detection',
      're-enqueue',
      'outage',
    ],
  },
  [SecretMenuTabs.sharepoint]: {
    label: 'SharePoint',
    description: 'Connect SharePoint and browse documents.',
    category: 'integrations',
    keywords: ['microsoft', 'oauth', 'remote docs'],
  },
  [SecretMenuTabs.openBanking]: {
    label: 'Open banking',
    description: 'Moneyhub accounts and transactions.',
    category: 'integrations',
    keywords: ['moneyhub', 'oauth'],
  },
  [SecretMenuTabs.microsoftEmail]: {
    label: 'Microsoft email',
    description: 'Send a real email through Microsoft.',
    category: 'integrations',
    keywords: ['outlook', 'oauth'],
  },
  [SecretMenuTabs.sandbox]: {
    label: 'Sandbox',
    description: 'Component demos on fixture data.',
    category: 'playground',
    keywords: ['ui', 'tip', 'email', 'connected assets', 'appendices'],
  },
  [SecretMenuTabs.editorPlayground]: {
    label: 'Editor playground',
    description: 'Markdown editor against a test document.',
    category: 'playground',
    keywords: ['lexical', 'suggestions'],
  },
  [SecretMenuTabs.topicTree]: {
    label: 'Topic tree',
    description: 'Cluster a transcript into a topic hierarchy.',
    category: 'playground',
    keywords: ['audio', 'meeting', 'leaf size'],
  },
  [SecretMenuTabs.transcriptTester]: {
    label: 'Transcript tester',
    description: 'Run audio through the meeting note pipeline.',
    category: 'playground',
    gate: 'afternoonOnly',
    keywords: ['recording', 'participants', 'meeting note template'],
  },
  [SecretMenuTabs.deriveFrnTest]: {
    label: 'FRN derivation test',
    description: 'Pull an FRN from an adviser website.',
    category: 'playground',
    keywords: ['fca', 'url'],
  },
  [SecretMenuTabs.autoBrandFromUrl]: {
    label: 'Auto brand from URL test',
    description: 'Extract brand colours from a URL.',
    category: 'playground',
    dangerous: true,
    keywords: ['branding', 'palette', 'logo', 'website'],
  },
  [SecretMenuTabs.billing]: {
    label: 'Billing',
    description: 'Billing summary with warnings per company.',
    category: 'ops',
    keywords: ['invoice', 'mrr', 'price', 'unit', 'non-advised'],
  },
  [SecretMenuTabs.usageMetrics]: {
    label: 'Usage metrics',
    description: 'Per-tenant usage counts, charted over time, with an xlsx export.',
    category: 'platform',
    gate: 'usageMetricsFlag',
    keywords: [
      'investor metrics',
      'clients',
      'suitability reports',
      'meeting notes',
      'agendas',
      'recordings',
      'files',
      'spreadsheet',
      'export',
    ],
  },
};

export type SecretMenuTool = {
  slug: SecretMenuTab;
  href: string;
  label: string;
  description: string;
  category: SecretMenuCategoryId;
  audience: SecretMenuAudience;
  dangerous: boolean;
  deprecated: boolean;
  gate: SecretMenuGate | undefined;
  searchText: string;
};

const tokenise = (value: string) =>
  value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

/**
 * A tool is searchable by its own words plus tokens taken from its slug and category, so a newly
 * added tab is findable before anyone has written keywords for it.
 */
const buildSearchText = (
  slug: SecretMenuTab,
  metadata: SecretMenuToolMetadata,
  category: SecretMenuCategory,
) => {
  const tokens = [
    ...tokenise(slug),
    ...tokenise(category.title),
    category.audience,
    ...(metadata.keywords ?? []),
  ];
  return [metadata.label, metadata.description, ...new Set(tokens)].join(' ');
};

const categoryFor = (id: SecretMenuCategoryId) => {
  const category = SECRET_MENU_CATEGORIES.find(candidate => candidate.id === id);
  if (!category) {
    throw new Error(`Unknown secret menu category: ${id}`);
  }
  return category;
};

export const SECRET_MENU_TOOLS: readonly SecretMenuTool[] = Object.entries(TOOL_METADATA).map(
  ([slug, metadata]) => {
    const category = categoryFor(metadata.category);
    return {
      slug: slug as SecretMenuTab,
      href: `/secret-menu/${slug}`,
      label: metadata.label,
      description: metadata.description,
      category: metadata.category,
      audience: category.audience,
      dangerous: metadata.dangerous === true,
      deprecated: metadata.deprecated === true,
      gate: metadata.gate,
      searchText: buildSearchText(slug as SecretMenuTab, metadata, category),
    };
  },
);

export type SecretMenuFilters = {
  query: string;
  audiences: readonly SecretMenuAudience[];
  markers: readonly SecretMenuMarker[];
};

const matchesMarkers = (tool: SecretMenuTool, markers: readonly SecretMenuMarker[]) =>
  markers.some(marker => (marker === 'dangerous' ? tool.dangerous : tool.deprecated));

/**
 * Pills within a group widen the results, pills across groups narrow them, and the query always
 * narrows further. Selecting nothing in a group leaves that group unfiltered.
 */
export const filterSecretMenuTools = (
  tools: readonly SecretMenuTool[],
  { query, audiences, markers }: SecretMenuFilters,
) =>
  tools.filter(tool => {
    if (audiences.length > 0 && !audiences.includes(tool.audience)) return false;
    if (markers.length > 0 && !matchesMarkers(tool, markers)) return false;
    return matchesAllTokens(tool.searchText, query);
  });
